import React, { MutableRefObject, useState } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import * as AppState from '../../../../AppState';
import classNames from 'classnames';
import PlacesAutocomplete from 'react-places-autocomplete';
import './AddressAutocomplete.scss';
import { SearchType } from '../../../../interfaces/interfaces';
import { useGetUserSearch } from '../../../../hooks/useUserSearchLocation';


// Custom hook tracks currently focused DOM element
const useActiveElement = () => {
	const [active, setActive] = useState(document.activeElement);

	const handleFocusIn = (e: any) => {
		setActive(document.activeElement);
	};

	React.useEffect(() => {
		document.addEventListener('focusin', handleFocusIn);
		return () => document.removeEventListener('focusin', handleFocusIn);
	}, []);

	return active;
};


const AddressAutocomplete = () => {
	const gsMapLoaded = useRecoilValue(AppState.gsMapScriptLoaded);
	const [showCurrentLocationError] = useState(false);
	const setGsUserSearch = useSetRecoilState(AppState.gsUserSearch);
	const [gsLocationAutoCompleteValue, setGsLocationAutoCompleteValue] = useRecoilState(AppState.gsLocationAutoCompleteValue);
	const [searchInputFocused, setSearchInputFocused] = useState(false);
	const [showCurrentLocationOption, setShowCurrentLocationOption] = useState(true);
	const [showMlsSearch, setShowMlsSearch] = useState(false);

	const [, setGsLocationToCurrentLocation] = useGetUserSearch();

	const activeElement = useActiveElement();
	const locationInput = document.getElementById('homeSearchLocationInput');
	const ref = React.useRef() as MutableRefObject<HTMLInputElement>;

	const autocompleteResultsOptions = {
		componentRestrictions: { country: 'us' },
		fields: ['address_components', 'geometry'],
		strictBounds: false,
		types: ['geocode'],
	};

	function searchEsByMlsNumber(inputValue: string) {
		const sanitizedString = inputValue.replace(/\s+/g, '');
		setGsUserSearch({ searchType: SearchType.MlsSearch, mlsId: sanitizedString });
	}


	// Allows for keyboard navigation when pressing 
	// 'Enter' after 'Search by MLS #' dropdown option 
	// is focused
	function handleKeyDownMlsSearch(event: any, inputValue: string) {
		if (event.key === 'Enter') {
			searchEsByMlsNumber(inputValue);
			setSearchInputFocused(false);
		}
	}


	// Allows for keyboard navigation when pressing 
	// 'Enter' after 'Current Location' dropdown option 
	// is focused
	function handleKeyDownCurrentLocation(event: any) {
		if (event.key === 'Enter') {
			getCurrentLocation();
			setSearchInputFocused(false);
			setGsLocationAutoCompleteValue('Current Location');
		}
	}


	// Hide dropdown if click off input
	function hideDropdownOffClick(event: any) {
		if (ref.current && !ref.current.contains(event.target as Node)) {
			setSearchInputFocused(false);
		}
	}


	// Show 'Current Location' option when input empty
	React.useEffect(() => {
		if (gsLocationAutoCompleteValue === '') {
			setShowCurrentLocationOption(true);
		} else setShowCurrentLocationOption(false);
	}, [gsLocationAutoCompleteValue]);


	// Show 'Search by MLS #' option when criteria met
	React.useEffect(() => {
		const stringLength = gsLocationAutoCompleteValue.length;
		const regex = /[a-zA-Z]/g;

		if (stringLength >= 7 && !regex.test(gsLocationAutoCompleteValue)) {
			setShowMlsSearch(true);
		} else setShowMlsSearch(false);
	}, [gsLocationAutoCompleteValue]);


	// Hide dropdown if neither input field, 'Current
	// Location', nor 'Search by MLS #' are focused
	React.useEffect(() => {
		const currentLocationOption = document.getElementById('currentLocationOption');
		const searchByMlsOption = document.getElementById('searchByMlsOption');

		if (activeElement === currentLocationOption || activeElement === searchByMlsOption || activeElement === locationInput) {
			setSearchInputFocused(true);
		} else setSearchInputFocused(false);
	}, [activeElement, locationInput]);


	// Listening for click event and cleaning up after
	React.useEffect(() => {
		document.addEventListener('click', hideDropdownOffClick);
		return () => document.removeEventListener('click', hideDropdownOffClick);
	});


	// Bubbles up to Map via Recoil values
	const getCurrentLocation = (): undefined => {
		setGsLocationAutoCompleteValue('Current location');
		setGsLocationToCurrentLocation();

		return undefined;
	};

	//Bubbles up to Map via Recoil values
	const getLocationFromSearch = async (autoCompleteSelection: string) => {
		setGsLocationAutoCompleteValue(autoCompleteSelection);
		setGsUserSearch({ searchType: SearchType.TextInput, currentAACValue: autoCompleteSelection }); // goes to location then to properties for map
	};

	React.useEffect(() => {
		const stringLength = gsLocationAutoCompleteValue.length;
		const regex = /[a-zA-Z]/g;

		if (stringLength >= 7 && !regex.test(gsLocationAutoCompleteValue)) {
			setShowMlsSearch(true);
		} else setShowMlsSearch(false);
	}, [gsLocationAutoCompleteValue]);

	return gsMapLoaded ? (
		<PlacesAutocomplete
			value={gsLocationAutoCompleteValue}
			onChange={e => setGsLocationAutoCompleteValue(e.valueOf())}
			onSelect={getLocationFromSearch}
			searchOptions={autocompleteResultsOptions}
			highlightFirstSuggestion
			debounce={500}
		>
			{({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
				<React.Fragment>
					<input
						ref={ref}
						{...getInputProps({
							id: 'homeSearchLocationInput',
							placeholder: 'City, zip, county, state, or MLS #',
							className: classNames(
								gsLocationAutoCompleteValue !== '' && 'pe-5',
								'form-control home-search__autocomplete-input',
							),
							onFocus: () => setSearchInputFocused(true),
						}) as any}
					/>

					<div className={classNames(showCurrentLocationError && 'show', 'home-search__location-input-error-message')}>
						<i className="fas fa-exclamation-triangle me-2" />
						Unable to retrieve location.
					</div>

					<div className="home-search__autocomplete-suggestion-container" role="menubar" aria-label="location input auto complete options">
						{searchInputFocused && (
							<>
								{(window.navigator.geolocation && showCurrentLocationOption) && ( // checks if device browser provides built-in Geolocation
									<div
										id="currentLocationOption"
										role="menuitem"
										tabIndex={0}
										className="home-search__autocomplete-item"
										onMouseDown={() => getCurrentLocation()}
										onKeyDown={(e: any) => handleKeyDownCurrentLocation(e)}
									>
										<div className="d-flex align-items-baseline">
											<i className="fa-map-marker-alt fad text-secondary me-2" />
											Use current location
										</div>
									</div>
								)}
								{showMlsSearch && (
									<div
										id="searchByMlsOption"
										role="menuitem"
										tabIndex={0}
										className="home-search__autocomplete-item"
										onMouseDown={() => searchEsByMlsNumber(gsLocationAutoCompleteValue)}
										onKeyDown={(e: any) => handleKeyDownMlsSearch(e, gsLocationAutoCompleteValue)}
									>
										<div className="d-flex align-items-baseline">
											<i className="fa-search fad text-secondary me-2" />
											Search by MLS #: {gsLocationAutoCompleteValue}
										</div>
									</div>
								)}

								{loading && (
									<div className="home-search__autocomplete-loading">
										<i className="fas fa-spinner fa-spin text-gray-500 me-2" />
										Loading addresses...
									</div>
								)}
							</>
						)}
						{suggestions.length > 0 &&
							suggestions.map((suggestion, index) => {
								return (
									<div
										{...getSuggestionItemProps(suggestion)}
										key={index}
										className={classNames(
											'home-search__autocomplete-item',
											suggestion.active && 'home-search__autocomplete-item--active',
										)}
									>
										<div className="d-flex align-items-baseline">
											<i
												className={classNames(
													suggestion.types.includes('administrative_area_level_1') // state
														? 'fa-flag-alt'
														: suggestion.types.includes('administrative_area_level_2') // county
															? 'fa-farm'
															: suggestion.types.includes('locality') // city
																? 'fa-city'
																: suggestion.types.includes('postal_code')
																	? 'fa-atlas'
																	: suggestion.types.includes('route') // route: a named route (such as "US 101")
																		? 'fa-road'
																		: suggestion.types.includes('street_address') || suggestion.types.includes('premise') // premise: a named location, usually a building or collection of buildings with a common name
																			? 'fa-home'
																			: 'fa-search',
													'fas text-gray-500 me-2',
												)}
											/>
											{suggestion.description}
										</div>
									</div>
								);
							})
						}
					</div>
				</React.Fragment>
			)}
		</PlacesAutocomplete>
	) : (
		<React.Fragment>
			<input
				placeholder="Google Autocomplete still loading..."
				className="form-control home-search__autocomplete-input disabled"
			/>
		</React.Fragment>
	);
};

export default AddressAutocomplete;
